import { EventEmitter, FunctionalComponent, VNode, h } from '@stencil/core';
import { Icon, SVGIcon, SVGIconData, getIconObject, getSVGIconObject } from '../../../core/interfaces/Icon';
import { generateSVGImage, isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/index';
import { getHyperLinkObject } from '../../../core/interfaces/Hyperlink';

import {
  DropdownBlurEvent,
  DropdownBlurHandler,
  DropdownChangeEvent,
  DropdownChangeHandler,
  DropdownFocusEvent,
  DropdownFocusHandler,
  DropdownOption,
} from './../../ads-dropdown.types';

interface AdsDropdownItemProps {
  item: DropdownOption;
  dropdownBlur?: EventEmitter<DropdownBlurEvent>;
  dropdownChange?: EventEmitter<DropdownChangeEvent>;
  dropdownFocus?: EventEmitter<DropdownFocusEvent>;
  dropdownBlurHandler?: DropdownBlurHandler;
  dropdownChangeHandler?: DropdownChangeHandler;
  dropdownFocusHandler?: DropdownFocusHandler;
}

export const AdsDropdownItem: FunctionalComponent<AdsDropdownItemProps> = ({
  item,
  dropdownBlur,
  dropdownChange,
  dropdownFocus,
  dropdownBlurHandler,
  dropdownChangeHandler,
  dropdownFocusHandler,
}) => {
  const onBlurHandler = (evt: FocusEvent): void => {
    if (!item.disabled) {
      dropdownBlur.emit({ evt: evt, value: item.value });
      !isNullOrUndefined(dropdownBlurHandler) ? dropdownBlurHandler({ evt: evt, value: item.value }) : null;
    } else {
      evt.preventDefault();
    }
  };

  const onFocusHandler = (evt: FocusEvent): void => {
    if (!item.disabled) {
      dropdownFocus.emit({ evt: evt, value: item.value });
      !isNullOrUndefined(dropdownFocusHandler) ? dropdownFocusHandler({ evt: evt, value: item.value }) : null;
    } else {
      evt.preventDefault();
    }
  };

  const onClickHandler = (evt: MouseEvent) => {
    if (!item.disabled) {
      dropdownChange.emit({ evt: evt, value: item.value });
      !isNullOrUndefined(dropdownChangeHandler) ? dropdownChangeHandler({ evt: evt, value: item.value }) : null;
      !isNullOrUndefined(linkObj) && linkObj.onClick(evt);
    } else {
      evt.preventDefault();
    }
  };

  const onKeyDownHandler = (evt: KeyboardEvent) => {
    if (!item.disabled) {
      if (evt.key === 'Enter') {
        dropdownChange.emit({ evt: evt, value: item.value });
        !isNullOrUndefined(dropdownChangeHandler) ? dropdownChangeHandler({ evt: evt, value: item.value }) : null;
        !isNullOrUndefined(linkObj) ? linkObj.onClick : evt.preventDefault();
      }
    }
  };

  const svgIconObj = getSVGIconObject(item.icon as SVGIcon);
  const iconObj = getIconObject(item.icon as Icon);
  const linkObj = getHyperLinkObject(item.link);
  const classes = ['c-dropdown__item'];
  let svgIcon: SVGIconData = undefined;
  let classIcon: string = undefined;

  svgIcon = svgIconObj?.active;
  classIcon = iconObj?.active;

  if (item.disabled) {
    classes.push('is-disabled');
  }

  const TagType = !isNullOrUndefined(item.link) && !item.disabled ? 'a' : 'span';

  const attributes: {
    id: string;
    role: string;
    tabindex: number;
    href?: string;
    hreflang?: string;
    rel?: string;
    target?: string;
    disabled: boolean;
    onBlur: (evt: FocusEvent) => void;
    onClick: (evt: MouseEvent) => void;
    onFocus: (evt: FocusEvent) => void;
    onKeyDown: (evt: KeyboardEvent) => void;
    'data-track'?: string;
    'data-track-value'?: string;
  } = {
    id: String(item.value),
    role: 'option',
    tabindex: item.disabled ? null : 0,
    disabled: item.disabled || null,
    onBlur: onBlurHandler,
    onClick: onClickHandler,
    onFocus: onFocusHandler,
    onKeyDown: isNullOrUndefined(item.link) ? onKeyDownHandler : null,
  };

  if (TagType === 'a') {
    attributes.href = linkObj?.href;
    attributes.hreflang = linkObj?.hreflang || null;
    attributes.rel = linkObj?.rel || null;
    attributes.target = linkObj?.target || null;
    attributes['data-track'] = 'true';
    attributes['data-track-value'] = !isNullOrUndefinedOrEmpty(item.text) ? item.text : item.value.toString();
  }

  return (
    <TagType class={classes.join(' ')} {...attributes}>
      {(classIcon || svgIcon) && (
        <span class="c-dropdown__item__icon">
          <span class={classIcon && classIcon}>
            {!isNullOrUndefined(svgIcon) && generateSVGImage({ data: svgIcon.data, height: svgIcon.height, width: svgIcon.width })}
          </span>
        </span>
      )}
      <span class="c-dropdown__item__label">{!isNullOrUndefinedOrEmpty(item.text) ? item.text : item.value}</span>
    </TagType>
  ) as VNode;
};
